import React from 'react';
import { useLocation } from 'react-router-dom';
import { AuthProvider } from './AuthContext';

interface ConditionalAuthProviderProps {
  children: React.ReactNode;
}

// Routes that should bypass authentication context entirely
// Use prefix matching for showcase routes, exact matching for others
const PUBLIC_ROUTE_PREFIXES = [
  '/showcase/',
  '/test-showcase/',
];

const PUBLIC_EXACT_ROUTES = [
  '/',
  '/about',
  '/verify-reset',
  '/reset-password',
  '/terms',
  '/privacy',
  '/cookies',
  '/disclaimer',
  '/pricing',
  '/features',
  '/data-protection',
  '/modern-slavery',
  '/environmental',
  '/bribery-policy',
  '/overview',
];

const isPublicRoute = (pathname: string): boolean => {
  // Check prefix matching for dynamic routes
  const hasPublicPrefix = PUBLIC_ROUTE_PREFIXES.some(prefix => 
    pathname.startsWith(prefix)
  );
  
  // Check exact matching for static routes
  const isExactMatch = PUBLIC_EXACT_ROUTES.includes(pathname);
  
  return hasPublicPrefix || isExactMatch;
};

export const ConditionalAuthProvider: React.FC<ConditionalAuthProviderProps> = ({ children }) => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Enhanced debugging for showcase routes
  console.log('========================================');
  console.log('🔐 [ConditionalAuth] ROUTE CHECK');
  console.log('🔐 [ConditionalAuth] Current pathname:', location.pathname);
  console.log('🔐 [ConditionalAuth] Is public route:', isPublic);
  console.log('🔐 [ConditionalAuth] Will initialize AuthProvider:', !isPublic);
  console.log('🔐 [ConditionalAuth] Timestamp:', new Date().toISOString());
  
  // Check if this matches any prefix
  const matchedPrefix = PUBLIC_ROUTE_PREFIXES.find(prefix => location.pathname.startsWith(prefix));
  const matchedExact = PUBLIC_EXACT_ROUTES.includes(location.pathname);
  console.log('🔐 [ConditionalAuth] Matched prefix:', matchedPrefix || 'none');
  console.log('🔐 [ConditionalAuth] Matched exact route:', matchedExact || 'none');
  console.log('========================================');

  // For public routes, render children directly without auth context
  if (isPublic) {
    console.log('✅ [ConditionalAuth] Rendering WITHOUT AuthProvider (public route)');
    return <>{children}</>;
  }

  // For authenticated routes, use full AuthProvider
  console.log('🔒 [ConditionalAuth] Rendering WITH AuthProvider (authenticated route)');
  return <AuthProvider>{children}</AuthProvider>;
};
